import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import { PageSection, SectionContent, SectionTitle, GreyButton } from '../styles/SharedStyles';

const ProductsContainer = styled.div`
  width: 100%;
  font-family: 'Montserrat', sans-serif;
`;

const Section = styled(PageSection)`
  background-color: #f9f9f9;
  min-height: calc(100vh - 60px);
`;

const ProductSectionContent = styled(SectionContent)`
  text-align: center;
`;

/* ---------- Thinki-specific styled components (kept for when Thinki is re-enabled) ---------- */
/*
const ProductCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 3rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const ThinkiLogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 2rem;
`;

const ThinkiLogo = styled.img`
  height: 60px;
  
  @media (max-width: 768px) {
    height: 50px;
  }
`;

const ProductDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: #666;
  margin-bottom: 2rem;
  text-align: center;
`;

const ProductNote = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #999;
  font-style: italic;
  margin-bottom: 2rem;
`;
*/
/* ---------- end Thinki styled components ---------- */

/* ---------- Downloads section styled components ---------- */
const DownloadsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 2.5rem;
  text-align: left;
`;

const DocumentCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const PdfIcon = styled.div`
  width: 34px;
  height: 34px;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const DocumentTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const DocumentDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  color: #666;
  margin: 0;
  flex: 1;
  text-align: left;
`;
/* ---------- end Downloads styled components ---------- */

const Products = () => {
  const { language } = useLanguage();

  const productsText = {
    en: {
      /* ---------- Thinki copy (kept for re-use) ---------- */
      /*
      title: 'Our Products',
      thinkiDescription: 'Thinki is an AI-powered learning platform that allows you to automatically generate and use learning materials.',
      thinkiNote: '(Thinki is currently available exclusively to our test group participants.)',
      visitButton: 'Visit Thinki',
      */
      /* ---------- end Thinki copy ---------- */
      downloadsTitle: 'Downloads',
      downloadButton: 'Download PDF',
      documents: [
        {
          id: 'doc-1',
          title: 'Content Creation Workflow',
          description: 'An example workflow for creating marketing content with ClaudeCowork. Intended as inspiration for integrating AI into your own content processes.',
          file: '/documents/Content_Creation_Workflow_PlixAI.pdf',
        },
        {
          id: 'doc-2',
          title: 'AI Potential Framework',
          description: 'A framework for identifying and evaluating AI potential within your organisation.',
          file: '/documents/KI_Potential_Framework_PlixAI.pdf',
        },
        {
          id: 'doc-3',
          title: 'AI-Guideline Checklist',
          description: 'A checklist to support the first steps in creating AI guidelines for your company.',
          file: '/documents/KI-Guidelines_PlixAI.pdf',
        },
        {
          id: 'doc-4',
          title: 'Prompt Templates',
          description: 'A collection of useful prompt templates for a variety of use cases.',
          file: '/documents/Prompt_Vorlagen_PlixAI.pdf',
        },
      ],
    },
    de: {
      /* ---------- Thinki copy (kept for re-use) ---------- */
      /*
      title: 'Unsere Produkte',
      thinkiDescription: 'Thinki ist eine KI-gestützte Lernplattform, mit der sich Lernmaterialien automatisch generieren und nutzen lassen.',
      thinkiNote: '(Thinki ist derzeit exklusiv für unsere Testgruppen-Teilnehmer verfügbar.)',
      visitButton: 'Besuche Thinki',
      */
      /* ---------- end Thinki copy ---------- */
      downloadsTitle: 'Downloads',
      downloadButton: 'PDF herunterladen',
      documents: [
        {
          id: 'doc-1',
          title: 'Content Creation Workflow',
          description: 'Dies ist ein beispielhafter Workflow für die Erstellung von Marketing-Inhalten mit ClaudeCowork. Dieser Workflow ist als Inspiration gedacht für die Integration in eigene Prozesse.',
          file: '/documents/Content_Creation_Workflow_PlixAI.pdf',
        },
        {
          id: 'doc-2',
          title: 'KI Potential Framework',
          description: 'Dieses Framework dient dazu, KI-Potentiale in einem Unternehmen zu identifizieren und zu bewerten.',
          file: '/documents/KI_Potential_Framework_PlixAI.pdf',
        },
        {
          id: 'doc-3',
          title: 'KI-Guideline Checkliste',
          description: 'Diese Checkliste unterstützt bei den ersten Schritten zur Erstellung von KI-Guidelines.',
          file: '/documents/KI-Guidelines_PlixAI.pdf',
        },
        {
          id: 'doc-4',
          title: 'Prompt Vorlagen',
          description: 'Einige nützliche Prompt Vorlagen für verschiedene Anwendungsfälle.',
          file: '/documents/Prompt_Vorlagen_PlixAI.pdf',
        },
      ],
    },
  };

  const t = productsText[language];

  return (
    <ProductsContainer>
      <Section id="downloads">
        <ProductSectionContent>
          <SectionTitle>{t.downloadsTitle}</SectionTitle>

          <DownloadsGrid>
            {t.documents.map((doc) => (
              <DocumentCard key={doc.id}>
                <PdfIcon>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#f0f0f0" stroke="#666" strokeWidth="1.5"/>
                    <path d="M14 2V8H20" stroke="#666" strokeWidth="1.5" strokeLinecap="round"/>
                    <text x="6.5" y="17" fontSize="5" fontWeight="bold" fill="#666" fontFamily="sans-serif">PDF</text>
                  </svg>
                </PdfIcon>
                <DocumentTitle>{doc.title}</DocumentTitle>
                <DocumentDescription>{doc.description}</DocumentDescription>
                <GreyButton href={doc.file} download target="_blank" rel="noopener noreferrer">
                  {t.downloadButton}
                </GreyButton>
              </DocumentCard>
            ))}
          </DownloadsGrid>
        </ProductSectionContent>
      </Section>

      {/* ---------- Thinki section (hidden for now – do not delete) ----------
      <Section id="thinki">
        <ProductSectionContent>
          <SectionTitle>{t.title}</SectionTitle>
          
          <ProductCard>
            <ThinkiLogoContainer>
              <ThinkiLogo src="/images/ThinkiLogo.svg" alt="Thinki Logo" />
            </ThinkiLogoContainer>
            
            <ProductDescription>
              {t.thinkiDescription}
            </ProductDescription>
            
            <ProductNote>
              {t.thinkiNote}
            </ProductNote>
            
            <TealButton href="https://www.thinki.de" target="_blank" rel="noopener noreferrer">
              {t.visitButton}
            </TealButton>
          </ProductCard>
        </ProductSectionContent>
      </Section>
      ---------- end Thinki section ---------- */}
    </ProductsContainer>
  );
};

export default Products;
